"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CarouselDotsList = void 0;
/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
const React = __importStar(require("react"));
const classnames_1 = __importDefault(require("classnames"));
const CarouselContext_js_1 = require("./CarouselContext.js");
const index_js_1 = require("../utils/index.js");
const CarouselDot_js_1 = require("./CarouselDot.js");
require("@itwin/itwinui-css/css/carousel.css");
/**
 * The `CarouselDotsList` component shows a list of `CarouselDot` components which can be used to
 * choose a specific slide. If used as a descendant of `Carousel`, then this component does not need
 * any props or `children`.
 *
 * The props can be specified if this component is being used outside `Carousel`. `children` can be specified
 * to override the individual dots that are shown.
 *
 * @example
 * <Carousel>
 *   // ...
 *   <Carousel.Dots />
 * </Carousel>
 *
 * @example
 * <Carousel.Dots
 *   length={10}
 *   maxCount={3}
 *   currentIndex={current}
 *   onSlideChange={(i) => setCurrent(i)}
 * />
 */
exports.CarouselDotsList = React.forwardRef((props, ref) => {
    var _a, _b, _c;
    const { currentIndex: userCurrentIndex, length, className, onSlideChange, children, ...rest } = props;
    (0, index_js_1.useTheme)();
    const context = React.useContext(CarouselContext_js_1.CarouselContext);
    const slideCount = (_a = length !== null && length !== void 0 ? length : context === null || context === void 0 ? void 0 : context.slideCount) !== null && _a !== void 0 ? _a : React.Children.count(children);
    const currentIndex = (_b = userCurrentIndex !== null && userCurrentIndex !== void 0 ? userCurrentIndex : context === null || context === void 0 ? void 0 : context.currentIndex) !== null && _b !== void 0 ? _b : 0;
    const idPrefix = (_c = props.id) !== null && _c !== void 0 ? _c : context === null || context === void 0 ? void 0 : context.idPrefix;
    const handleSlideChange = React.useCallback((index) => {
        if (context) {
            context.setCurrentIndex(index);
            context.scrollToSlide.current(index);
        }
        onSlideChange === null || onSlideChange === void 0 ? void 0 : onSlideChange(index);
    }, [context, onSlideChange]);
    const justMounted = React.useRef(true);
    const [visibleCount, setVisibleCount] = React.useState(slideCount);
    const listRef = React.useRef(null);
    const [width, setWidth] = React.useState();
    const [resizeRef, resizeObserver] = (0, index_js_1.useResizeObserver)(({ width }) => {
        if (!listRef.current) {
            return;
        }
        setWidth(width);
        const dotWidth = listRef.current.children[0].offsetWidth;
        setVisibleCount(Math.floor(width / dotWidth));
    });
    React.useEffect(() => resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.disconnect(), [resizeObserver]);
    const refs = (0, index_js_1.useMergedRefs)(ref, resizeRef, listRef);
    const firstVisibleDotIndex = React.useMemo(() => (0, index_js_1.getBoundedValue)(currentIndex - Math.ceil(visibleCount / 2) + 1, 0, slideCount - visibleCount), [currentIndex, slideCount, visibleCount]);
    const lastVisibleDotIndex = React.useMemo(() => (0, index_js_1.getBoundedValue)(currentIndex + Math.floor(visibleCount / 2), visibleCount - 1, slideCount - 1), [currentIndex, slideCount, visibleCount]);
    const dots = React.useMemo(() => {
        return Array(slideCount)
            .fill(null)
            .map((_, index) => {
            const isFirstSmallDot = (index === firstVisibleDotIndex && index !== 0) ||
                (index === lastVisibleDotIndex && index !== slideCount - 1);
            const isSecondSmallDot = (index === firstVisibleDotIndex + 1 && index !== 1) ||
                (index === lastVisibleDotIndex - 1 && index !== slideCount - 2);
            const isClipped = index < firstVisibleDotIndex || index > lastVisibleDotIndex;
            return (React.createElement(CarouselDot_js_1.CarouselDot, { key: index, "aria-label": `Slide ${index}`, isActive: index === currentIndex, onClick: () => handleSlideChange(index), isSmall: isSecondSmallDot, isSmaller: isFirstSmallDot || isClipped, id: idPrefix && `${idPrefix}--dot-${index}`, "aria-controls": idPrefix && `${idPrefix}--slide-${index}` }));
        });
    }, [
        slideCount,
        firstVisibleDotIndex,
        lastVisibleDotIndex,
        currentIndex,
        idPrefix,
        handleSlideChange,
    ]);
    React.useEffect(() => {
        var _a, _b, _c;
        const firstDot = (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.children[firstVisibleDotIndex];
        if (!listRef.current || !firstDot) {
            return;
        }
        const motionOk = (_c = (_b = (0, index_js_1.getWindow)()) === null || _b === void 0 ? void 0 : _b.matchMedia('(prefers-reduced-motion: no-preference)')) === null || _c === void 0 ? void 0 : _c.matches;
        listRef.current.scrollTo({
            left: firstDot.offsetLeft - listRef.current.offsetLeft,
            behavior: motionOk && !justMounted.current ? 'smooth' : 'auto',
        });
        if (justMounted.current) {
            justMounted.current = false;
        }
    }, [currentIndex, firstVisibleDotIndex, slideCount, visibleCount, width]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: (0, classnames_1.default)('iui-carousel-navigation-dots', className), role: 'tablist', "aria-label": 'Slides', ref: refs, ...rest }, children !== null && children !== void 0 ? children : dots)));
});
